<template>
  <div class="reference-list-page">
    <div class="reference-list-page__filter">
      <b-form-input
        v-model.trim="referenceFilterQuery"
        placeholder="Поиск по названию"
      />
    </div>

    <b-card
      v-for="reference in filteredReferences"
      :key="reference.image"
      class="reference-list-page__card"
      no-body
      role="button"
      tabindex="0"
      @click="openPreview(reference)"
      @keydown.enter.prevent="openPreview(reference)"
      @keydown.space.prevent="openPreview(reference)"
    >
      <div class="reference-list-page__image-wrap">
        <img
          class="reference-list-page__image"
          :src="getImageSrc(reference.image)"
          :alt="reference.name"
        >
      </div>
      <div class="reference-list-page__name">
        {{ reference.name }}
      </div>
    </b-card>

    <div
      v-if="selectedReference"
      class="reference-list-page__preview"
      @click.self="closePreview"
      @wheel.prevent="handlePreviewWheel"
    >
      <div class="reference-list-page__preview-toolbar">
        <div class="reference-list-page__preview-title">
          {{ selectedReference.name }}
        </div>
        <div class="reference-list-page__preview-controls">
          <b-button
            class="reference-list-page__preview-button"
            variant="flat-light"
            :disabled="previewZoom <= minPreviewZoom"
            title="Уменьшить"
            aria-label="Уменьшить"
            @click.stop="zoomOut"
          >
            <feather-icon
              icon="ZoomOutIcon"
              size="18"
            />
          </b-button>
          <b-button
            class="reference-list-page__preview-button"
            variant="flat-light"
            title="Сбросить масштаб"
            aria-label="Сбросить масштаб"
            @click.stop="resetZoom"
          >
            <feather-icon
              icon="MaximizeIcon"
              size="18"
            />
          </b-button>
          <b-button
            class="reference-list-page__preview-button"
            variant="flat-light"
            :disabled="previewZoom >= maxPreviewZoom"
            title="Увеличить"
            aria-label="Увеличить"
            @click.stop="zoomIn"
          >
            <feather-icon
              icon="ZoomInIcon"
              size="18"
            />
          </b-button>
          <b-button
            class="reference-list-page__preview-button"
            variant="flat-light"
            title="Закрыть"
            aria-label="Закрыть"
            @click.stop="closePreview"
          >
            <feather-icon
              icon="XIcon"
              size="20"
            />
          </b-button>
        </div>
      </div>
      <div
        ref="previewBody"
        class="reference-list-page__preview-body"
        :class="{
          'reference-list-page__preview-body--draggable': isPreviewDraggable,
          'reference-list-page__preview-body--dragging': isPreviewDragging,
        }"
        @click.self="closePreview"
        @pointerdown="startPreviewPan"
      >
        <img
          class="reference-list-page__preview-image"
          :src="selectedReferenceImage"
          :alt="selectedReference.name"
          :style="previewImageStyle"
          draggable="false"
          @dragstart.prevent
        >
      </div>
    </div>
    <Preloader v-if="loading" />
  </div>
</template>

<script>
import { BButton, BCard, BFormInput } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import Preloader from '@/components/Preloader.vue'

export default {
  name: 'ReferenceListPage',

  components: {
    BButton,
    BCard,
    BFormInput,
    Preloader,
  },

  data() {
    return {
      loading: false,
      references: [],
      referenceFilterQuery: '',
      selectedReference: null,
      previewZoom: 1,
      minPreviewZoom: 0.5,
      maxPreviewZoom: 5,
      previewZoomStep: 0.25,
      isPreviewDragging: false,
      previewPanStartX: 0,
      previewPanStartY: 0,
      previewPanScrollLeft: 0,
      previewPanScrollTop: 0,
    }
  },

  computed: {
    ...mapGetters('app', {
      apiAddr: 'apiAddr',
    }),
    project() {
      return this.$route.meta.project
    },
    normalizedReferenceFilterQuery() {
      return this.referenceFilterQuery.toLowerCase()
    },
    filteredReferences() {
      if (this.normalizedReferenceFilterQuery.length < 3) {
        return this.references
      }

      return this.references.filter(reference => (
        reference.name || ''
      ).toLowerCase().includes(this.normalizedReferenceFilterQuery))
    },
    selectedReferenceImage() {
      return this.selectedReference ? this.getImageSrc(this.selectedReference.image) : ''
    },
    previewImageStyle() {
      return {
        width: `${90 * this.previewZoom}vw`,
        height: `calc((100vh - 7rem) * ${this.previewZoom})`,
      }
    },
    isPreviewDraggable() {
      return this.previewZoom > 1
    },
  },

  mounted() {
    this.loadReferenceList()
  },

  beforeDestroy() {
    this.removePreviewListeners()
  },

  methods: {
    loadReferenceList() {
      this.loading = true
      fetch(`${this.apiAddr}${this.project}/api/reference-list`)
        .then(res => (res.status === 200 ? res.json() : []))
        .then(references => {
          this.references = Array.isArray(references) ? references : []
        })
        .catch(() => {
          this.references = []
        })
        .finally(() => {
          this.loading = false
        })
    },

    getImageSrc(image) {
      if (!image) {
        return ''
      }

      if (/^https?:\/\//.test(image)) {
        return image
      }

      return `${this.apiAddr}${this.project}${image.startsWith('/') ? image : `/${image}`}`
    },

    openPreview(reference) {
      this.selectedReference = reference
      this.previewZoom = 1
      document.addEventListener('keydown', this.handlePreviewKeydown)
    },

    closePreview() {
      this.selectedReference = null
      this.previewZoom = 1
      this.removePreviewListeners()
    },

    removePreviewListeners() {
      document.removeEventListener('keydown', this.handlePreviewKeydown)
      document.removeEventListener('pointermove', this.handlePreviewPan)
      document.removeEventListener('pointerup', this.stopPreviewPan)
      document.removeEventListener('pointercancel', this.stopPreviewPan)
      this.isPreviewDragging = false
    },

    handlePreviewKeydown(event) {
      if (event.key === 'Escape') {
        this.closePreview()
      }

      if (event.key === '+' || event.key === '=') {
        this.zoomIn()
      }

      if (event.key === '-') {
        this.zoomOut()
      }

      if (event.key === '0') {
        this.resetZoom()
      }
    },

    handlePreviewWheel(event) {
      if (event.deltaY < 0) {
        this.zoomIn()
        return
      }

      this.zoomOut()
    },

    startPreviewPan(event) {
      if (!this.isPreviewDraggable || event.button !== 0) {
        return
      }

      const { previewBody } = this.$refs

      if (!previewBody) {
        return
      }

      this.isPreviewDragging = true
      this.previewPanStartX = event.clientX
      this.previewPanStartY = event.clientY
      this.previewPanScrollLeft = previewBody.scrollLeft
      this.previewPanScrollTop = previewBody.scrollTop
      event.preventDefault()

      document.addEventListener('pointermove', this.handlePreviewPan)
      document.addEventListener('pointerup', this.stopPreviewPan)
      document.addEventListener('pointercancel', this.stopPreviewPan)
    },

    handlePreviewPan(event) {
      if (!this.isPreviewDragging) {
        return
      }

      const { previewBody } = this.$refs

      if (!previewBody) {
        return
      }

      previewBody.scrollLeft = this.previewPanScrollLeft - (event.clientX - this.previewPanStartX)
      previewBody.scrollTop = this.previewPanScrollTop - (event.clientY - this.previewPanStartY)
    },

    stopPreviewPan() {
      this.isPreviewDragging = false
      document.removeEventListener('pointermove', this.handlePreviewPan)
      document.removeEventListener('pointerup', this.stopPreviewPan)
      document.removeEventListener('pointercancel', this.stopPreviewPan)
    },

    zoomIn() {
      this.previewZoom = Math.min(
        this.maxPreviewZoom,
        this.previewZoom + this.previewZoomStep,
      )
    },

    zoomOut() {
      this.previewZoom = Math.max(
        this.minPreviewZoom,
        this.previewZoom - this.previewZoomStep,
      )
    },

    resetZoom() {
      this.previewZoom = 1
    },
  },
}
</script>

<style scoped lang="scss">
.reference-list-page {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  grid-gap: 1.5rem;

  &__filter {
    grid-column: 1 / -1;
  }

  &__card {
    display: flex;
    flex-direction: column;
    height: 280px;
    cursor: pointer;
    overflow: hidden;
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover,
    &:focus {
      transform: translateY(-2px);
      box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
      outline: none;
    }
  }

  &__image-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 190px;
    padding: 1rem;
    background: #f8f8f8;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__name {
    display: flex;
    align-items: center;
    flex: 1;
    padding: 1rem;
    font-weight: 600;
  }

  &__preview {
    position: fixed;
    z-index: 2000;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    background: rgba(22, 29, 49, 0.9);
  }

  &__preview-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex: 0 0 auto;
    min-height: 44px;
    margin-bottom: 1rem;
  }

  &__preview-title {
    min-width: 0;
    color: #fff;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__preview-controls {
    display: flex;
    gap: 0.5rem;
    flex: 0 0 auto;
  }

  &__preview-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    color: #fff;
    border-color: rgba(255, 255, 255, 0.24);
    background: rgba(255, 255, 255, 0.12);

    &:hover,
    &:focus {
      color: #fff;
      background: rgba(255, 255, 255, 0.2);
    }
  }

  &__preview-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;

    &--draggable {
      cursor: grab;
    }

    &--dragging {
      cursor: grabbing;
      user-select: none;
    }
  }

  &__preview-image {
    display: block;
    flex: 0 0 auto;
    margin: auto;
    object-fit: contain;
    transition: width 0.15s ease, height 0.15s ease;
  }
}
</style>
